import React, {forwardRef} from 'react';
import {styled} from '@mui/material/styles';
import {useId} from '../hooks/useId';
import {Box} from '../Box/Box';
import {AccordionItem} from './AccordionItem';
import type {AccordionProps as MuiAccordionProps} from '@mui/material/Accordion';
import {excludeComponentProps} from '../utils/excludeComponentProps';

type DisabledAccordionProps = 'disableGutters' | 'elevation' | 'children' | 'square' | 'onChange';

/** NOTE: These props _will_ be passed to child AccordionItems */
export interface AccordionGlobalProps extends Omit<MuiAccordionProps, DisabledAccordionProps> {
    /**
     * If `true`, all the child AccordionItems will be right aligned.
     * @default false
     */
    rightAligned?: boolean;
    /**
     * If `true`, all child AccordionItems will have less padding.
     * @default false
     */
    dense?: boolean;
    /**
     * Will use the given string to generate a11y standard ids
     * @default undefined
     */
    generatedId?: string;
    /**
     * The content of this component.
     */
    children?: MuiAccordionProps['children'];
}

/** NOTE: These props will _not_ be passed to child AccordionItems */
export interface AccordionProps extends Omit<AccordionGlobalProps, 'generatedId'> {
    /**
     * If `true`, space will be added between the child AccordionItems.
     * @default false
     */
    spaced?: boolean;
    /**
     * Determines the spacing between AccordionItems. Will only take effect if `spaced = true`
     * @default '0.75rem'
     */
    spacing?: string | number;
}

const StyledAccordion = styled(Box, {
    ...excludeComponentProps('variant', 'spaced', 'spacing')
})<AccordionProps>(({
    theme, spaced, spacing, variant
}) => ({
    borderRadius: theme.tokens.FuelBorderRadiusLg,
    overflow: 'hidden',

    // default
    boxShadow: theme.tokens.FuelShadowMd,
    border: 'none',

    ...variant === 'elevated' && {
        boxShadow: theme.tokens.FuelShadowMd,
        border: 'none'
    },

    ...variant === 'outlined' && {
        boxShadow: 'none',
        border: `solid 1px ${theme.tokens.FuelColorBorderLight}`
    },

    ...spaced && {
        boxShadow: undefined,
        border: undefined,
        borderRadius: undefined,
        overflow: undefined
    },

    '& > .MuiAccordion-root': {
        border: 'none',
        borderRadius: '0',
        boxShadow: 'none',
        zIndex: 10,

        ...spaced && {
            boxShadow: undefined,
            border: undefined,
            borderRadius: undefined
        },

        // default
        '&:not(:last-of-type)': {
            borderBottom: `solid 1px ${theme.tokens.FuelColorBorderLight}`,

            ...spaced && {
                marginBottom: spacing || '0.75rem', // setting a default value here again.
                borderBottom: 'none'
            }
        },

        '&:hover': {
            zIndex: 10
        }
    }
}));

type AccordionChildrenType = AccordionProps['children'] | AccordionGlobalProps | React.ReactChild;

export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(({
    children, rightAligned, defaultExpanded, disabled, spaced, spacing = '0.75rem', dense,
    TransitionComponent, TransitionProps, expanded, variant = 'elevated',
    id, ...props
}, ref) => {
    const generatedId = useId(id, 'accordion');

    return (
        <StyledAccordion
            ref={ref}
            spaced={spaced}
            spacing={spacing}
            variant={variant}
            {...props}
            id={`${generatedId}-group`}
        >
            {React.Children.toArray(children).map<AccordionChildrenType>((child, index) => {
                if (React.isValidElement(child)) {
                    if (child.type === AccordionItem) {
                        return React.cloneElement(child, {
                            variant,
                            rightAligned,
                            defaultExpanded,
                            disabled,
                            TransitionComponent,
                            TransitionProps,
                            expanded,
                            dense,
                            ...child.props, // child props to override cloned props
                            /** Props below this line cannot be overwritten */
                            generatedId: `${generatedId}-${index}`
                        });
                    }
                }

                return child;
            })}
        </StyledAccordion>
    );
});

Accordion.displayName = 'Accordion';
